import { Eye } from "lucide-react";
import type { Schedule, Slideshow } from "../types";
import { getBlockPositionHorizontal, formatTime } from "../utils";

type Props = {
  schedule: Schedule;
  slideshow: Slideshow | undefined;
  isSelected: boolean;
  isDragging: boolean;
  isEditable: boolean;
  overlapRow: number;
  overlapTotalRows: number;
  onSelect: () => void;
  onStartResize: (edge: "left" | "right") => void;
  onStartDrag: (clickX: number) => void;
};

export default ({
  schedule,
  slideshow,
  isSelected,
  isDragging,
  isEditable,
  overlapRow,
  overlapTotalRows,
  onSelect,
  onStartResize,
  onStartDrag,
}: Props) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  const handleResizeStart = (e: React.MouseEvent, edge: "left" | "right") => {
    if (!isEditable) return;
    e.preventDefault();
    e.stopPropagation();
    onStartResize(edge);
  };

  const handleDragStart = (e: React.MouseEvent) => {
    if (!isEditable) return;
    e.preventDefault();
    e.stopPropagation();
    onStartDrag(e.clientX);
  };

  const color = slideshow?.color || "#666";

  const zIndex = isSelected ? 30 : 10;
  const opacityHex = "66"; // ~40%

  // Simple stacking: 50px per block, 2px gap
  const hasOverlap = overlapTotalRows > 1;
  const blockHeight = hasOverlap ? 50 : 76;
  const gap = 2;
  const top = overlapRow * (50 + gap) + gap;

  return (
    <div
      onClick={handleClick}
      className={`absolute left-1 right-1 rounded-lg overflow-hidden transition-all ${
        isSelected ? "ring-2 ring-white" : "hover:brightness-110"
      }`}
      style={{
        ...getBlockPositionHorizontal(schedule.startTime, schedule.endTime),
        backgroundColor: `${color}${opacityHex}`,
        border: `2px dashed ${color}`,
        zIndex,
        top,
        height: blockHeight,
      }}
    >
      {/* Left resize handle */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-3 z-20 ${isEditable ? "cursor-w-resize" : "cursor-not-allowed"}`}
        style={{ backgroundColor: "transparent" }}
        onMouseDown={(e) => handleResizeStart(e, "left")}
        onMouseEnter={(e) => isEditable && (e.currentTarget.style.backgroundColor = `${color}40`)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      />

      {/* Draggable content area */}
      <div
        onMouseDown={handleDragStart}
        className={`absolute left-3 right-3 top-0 bottom-0 ${
          !isEditable ? "cursor-not-allowed" : isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        <div className="h-full flex items-center gap-2 px-3 overflow-hidden">
          <div className="font-medium text-sm truncate text-white">{schedule.name}</div>
          <div className="text-xs whitespace-nowrap flex-shrink-0 text-zinc-300">
            {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
          </div>
          {!schedule.canEdit && (
            <span title="View only">
              <Eye className="w-3.5 h-3.5 flex-shrink-0 text-zinc-400" />
            </span>
          )}
        </div>
      </div>

      {/* Right resize handle */}
      <div
        className={`absolute right-0 top-0 bottom-0 w-3 z-20 ${isEditable ? "cursor-e-resize" : "cursor-not-allowed"}`}
        style={{ backgroundColor: "transparent" }}
        onMouseDown={(e) => handleResizeStart(e, "right")}
        onMouseEnter={(e) => isEditable && (e.currentTarget.style.backgroundColor = `${color}40`)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      />
    </div>
  );
};
